import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {resetPassword} from '../core/_requests'

const initialValues = {
  password: '',
  password_confirmation: '',
}

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'La contraseña requiere mínimo 6 caracteres')
    .matches(/^[a-zA-Z0-9]*([a-zA-Z][0-9]|[0-9][a-zA-Z])[a-zA-Z0-9]*$/, 'La contraseña debe tener números y letras')
    .required('La contraseña es obligatoria'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Debe confirmar la contraseña'),
})

export function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [success, setSuccess] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: (values, {setSubmitting}) => {
      if (!token) {
        setErrorMessage('El link de recuperación no es válido, favor solicite uno nuevo.')
        return
      }
      setLoading(true)
      setErrorMessage(undefined)
      resetPassword(token, values.password, values.password_confirmation)
        .then(() => {
          setLoading(false)
          setSuccess(true)
          setTimeout(() => navigate('/auth/login'), 3000)
        })
        .catch((error: any) => {
          setLoading(false)
          setSubmitting(false)
          setErrorMessage(
            error?.response?.data?.message ||
              'No fue posible actualizar la contraseña, favor solicite un nuevo link.'
          )
        })
    },
  })

  if (!token) {
    return (
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>Link inválido</h1>
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            El link de recuperación no es válido o está incompleto.
          </div>
        </div>
        <Link to='/auth/forgot-password' className='btn btn-primary'>
          Solicitar nuevo link
        </Link>
      </div>
    )
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework p-4'
      noValidate
      id='kt_reset_password_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-primary fw-bolder mb-3'>Nueva contraseña</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Ingrese su nueva contraseña.</div>
      </div>

      {errorMessage && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{errorMessage}</div>
        </div>
      )}

      {success && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Contraseña actualizada correctamente, será redirigido al inicio de sesión.</div>
        </div>
      )}

      {!success && (
        <>
          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-700 fs-6'>Nueva contraseña</label>
            <input
              type='password'
              placeholder=''
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.password && formik.errors.password},
                {'is-valid': formik.touched.password && !formik.errors.password}
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-700 fs-6'>Confirmar contraseña</label>
            <input
              type='password'
              placeholder=''
              autoComplete='off'
              {...formik.getFieldProps('password_confirmation')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password_confirmation && formik.errors.password_confirmation,
                },
                {
                  'is-valid': formik.touched.password_confirmation && !formik.errors.password_confirmation,
                }
              )}
            />
            {formik.touched.password_confirmation && formik.errors.password_confirmation && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password_confirmation}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
            <button
              type='submit'
              id='kt_reset_password_submit'
              className='btn btn-primary me-4'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {loading ? (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Por favor espere...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              ) : (
                <span className='indicator-label'>Guardar</span>
              )}
            </button>
            <Link to='/auth/login'>
              <button type='button' className='btn btn-light' disabled={formik.isSubmitting}>
                Cancelar
              </button>
            </Link>
          </div>
          {/* end::Form group */}
        </>
      )}
    </form>
  )
}
